import React, { useEffect, useRef, useState, useCallback } from "react";
import { Icons } from "../../../icons";
import { Switch } from "../Switch";
import {
  SelectContainer,
  SelectButton,
  SelectTopRow,
  SelectControls,
  SelectArrow,
  SelectDropdown,
  MultiSelectTags,
  SwitchContainer,
  SwitchAction,
  TagsBar,
  ScrollButton,
  InlineSearchInput,
  OptionsLoading,
  SmallSpinner,
} from "./Select.styles";
import { Label } from "../Input/Input.styles";
import { useSelect } from "./useSelect";
import type {
  SelectAppearance,
  SelectOption,
  SelectProps,
} from "./Select.types";
import {
  ClearButton,
  SearchInput,
  TagButton,
  SelectOption as SelectOptionComponent,
  EmptyState,
  SelectAllButton,
} from "./components";

// Re-export types for backward compatibility
export type {
  SelectAppearance,
  SelectOption,
  SelectProps,
};

export const Select: React.FC<SelectProps & { label?: string }> = ({
  options,
  value,
  label,
  required = false,
  placeholder = "Seleccioná una opción",
  onChange,
  variant = "single",
  appearance = "default",
  disabled = false,
  searchable = false,
  clearable = false,
  loading = false,
  className,
  id,
  maxHeight = 200,
  maxSelections,
  showSelectAll = false,
  showAllOption = false,
  optionLabel = "Todos",
  onAllToggle,
  onSearch,
  searchDebounceMs = 300,
  minSearchLength = 0,
  empty = false,
  emptyMessage = "No hay opciones disponibles",
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
}) => {
  const {
    isOpen,
    searchTerm,
    selectedValues,
    selectedOptions,
    filteredOptions,
    allSelected,
    hasValue,
    isMulti,
    handleToggle,
    handleOptionClick,
    handleClear,
    handleSelectAll,
    handleDeselectAll,
    handleAllToggle,
    handleRemoveTag,
    handleKeyDown,
    handleSearchChange,
    selectRef,
    searchInputRef,
    setSearchTerm,
  } = useSelect({
    options,
    value,
    variant,
    disabled,
    loading,
    searchable,
    maxSelections,
    showSelectAll,
    showAllOption,
    optionLabel: optionLabel,
    onSearch,
    searchDebounceMs,
    minSearchLength,
    onChange,
    onAllToggle,
  });

  // Display text with placeholder fallback
  const displayText = loading
    ? "Cargando..."
    : isMulti && searchTerm
      ? searchTerm
      : hasValue && !isMulti && selectedOptions[0]
        ? selectedOptions[0].label
        : placeholder;

  const showClearButton =
    clearable && (hasValue || searchTerm.length > 0) && !disabled && !loading;
  const showInlineSearch = searchable && isOpen;
  const showTagsBar = isMulti && (allSelected || selectedOptions.length > 0);
  const handleClearSelection = useCallback(
    (event: React.MouseEvent) => {
      if (hasValue) {
        handleClear(event);
      } else {
        event.stopPropagation();
      }
      setSearchTerm("");
    },
    [handleClear, hasValue, setSearchTerm],
  );

  // ----- Scroll horizontal de tags -----
  const tagsRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const computeOverflow = useCallback(() => {
    const el = tagsRef.current;
    if (!el) return;
    // margen pequeño por redondeos
    const sl = Math.round(el.scrollLeft);
    const max = Math.round(el.scrollWidth - el.clientWidth);
    setShowLeft(sl > 0);
    setShowRight(sl < max);
  }, []);

  const handleTagsScroll = useCallback(
    () => computeOverflow(),
    [computeOverflow],
  );

  const scrollTags = useCallback((dir: "left" | "right") => {
    const el = tagsRef.current;
    if (!el) return;
    const amount = Math.max(120, Math.floor(el.clientWidth * 0.6));
    el.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  }, []);

  // Recalcular cuando cambian las selecciones o el layout
  useEffect(() => {
    computeOverflow();
  }, [computeOverflow, selectedOptions.length]);

  useEffect(() => {
    const onResize = () => computeOverflow();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [computeOverflow]);

  return (
    <SelectContainer ref={selectRef} className={className}>
      {label ? (
        <Label $required={required}>{label}</Label>
      ) : null}
      {/* Switch para "Todos" arriba del input */}
      {isMulti && showAllOption && (
        <SwitchContainer>
          <span>{optionLabel}</span>
          <SwitchAction>
            <span>Todos</span>
            <Switch
              isChecked={allSelected}
              onChange={() => {
                handleAllToggle();
                if (!allSelected) {
                  setSearchTerm("");
                }
              }}
              disabled={
                disabled ||
                loading ||
                options.length === 0 ||
                empty ||
                (filteredOptions.length === 0 && !loading)
              }
              aria-label={`Toggle all ${optionLabel}`}
            />
          </SwitchAction>
        </SwitchContainer>
      )}

      <SelectButton
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={allSelected ? undefined : handleToggle}
        onKeyDown={handleKeyDown}
        $isDisabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        id={id}
        data-testid={id ? `select-${id}` : "select-base"}
        $hasValue={hasValue}
        $isMulti={isMulti}
        $hasSearchTerm={!!searchTerm}
        $appearance={appearance}
      >
        {/* Top row with text and controls */}
        <SelectTopRow>
          {showInlineSearch ? (
            <InlineSearchInput
              ref={searchInputRef}
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={placeholder || "Buscar..."}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              $appearance={appearance}
            />
          ) : (
            <span
              translate="no"
            >{displayText}</span>
          )}
          <SelectControls>
            {showClearButton && (
              <ClearButton
                onClear={handleClearSelection}
                disabled={disabled || loading}
              />
            )}
            {loading ? (
              <SmallSpinner />
            ) : (
              <SelectArrow $isOpen={isOpen} $appearance={appearance}>
                <Icons
                  name="iconArrow400"
                  width={22}
                  height={22}
                  color="currentColor"
                />
              </SelectArrow>
            )}
          </SelectControls>
        </SelectTopRow>
      </SelectButton>

      {/* 🎯 Barra de tags: altura fija, no crece; scroll horizontal con botones */}
      {showTagsBar && (
        <TagsBar>
          {showLeft && (
            <ScrollButton
              type="button"
              aria-label="Desplazar a la izquierda"
              data-side="left"
              onClick={() => scrollTags("left")}
            >
              <Icons name="iconArrow400" width={18} height={18} />
            </ScrollButton>
          )}

          <MultiSelectTags
            ref={tagsRef}
            data-testid="multi-select-tags"
            onScroll={handleTagsScroll}
            showLeft={showLeft}
            showRight={showRight}
          >
            {allSelected ? (
              <TagButton
                key="__ALL__"
                label="Todos"
                value="__ALL__"
                onRemove={handleRemoveTag}
                disabled={disabled || loading}
              />
            ) : (
              selectedOptions.map((option) => (
                <TagButton
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  onRemove={handleRemoveTag}
                  disabled={disabled || loading}
                />
              ))
            )}
          </MultiSelectTags>

          {showRight && (
            <ScrollButton
              type="button"
              aria-label="Desplazar a la derecha"
              data-side="right"
              onClick={() => scrollTags("right")}
            >
              <Icons name="iconArrow400" width={18} height={18} />
            </ScrollButton>
          )}
        </TagsBar>
      )}

      {isOpen && (
        <SelectDropdown
          role="listbox"
          aria-multiselectable={isMulti}
          $appearance={appearance}
          style={{ maxHeight: `${maxHeight}px` }}
        >
          {searchable && !showInlineSearch && (
            <SearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              inputRef={searchInputRef}
            />
          )}

          {isMulti &&
            showSelectAll &&
            !showAllOption &&
            filteredOptions.length > 1 && (
              <SelectAllButton
                isAllSelected={selectedValues.length === filteredOptions.length}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
              />
            )}

          {loading && (
            <OptionsLoading $appearance={appearance}>
              <SmallSpinner /> Cargando opciones...
            </OptionsLoading>
          )}

          {filteredOptions.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            const isDisabled =
              option.disabled || (showAllOption && allSelected);

            return (
              <SelectOptionComponent
                key={option.value}
                option={option}
                isSelected={isSelected}
                isDisabled={isDisabled}
                isMulti={isMulti}
                allSelected={allSelected}
                appearance={appearance}
                onClick={() => handleOptionClick(option.value)}
              />
            );
          })}

          {/* Empty state */}
          {!loading && filteredOptions.length === 0 && (
            <EmptyState
              searchTerm={searchTerm}
              empty={empty}
              emptyMessage={emptyMessage}
              appearance={appearance}
            />
          )}
        </SelectDropdown>
      )}
    </SelectContainer>
  );
};

export default Select;
